import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";

const FinalCTA = () => {
  const navigate = useNavigate();
  
  // CONFIGURATION
  const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbzoOu3omBoH_inj9IXstqd2EGcqnne3ej6JCaxjsW1SaNarwL4BmS2gyszZlyfgGow/exec';
  const DOWNLOAD_FILE_URL = '/E-book.pdf';

  // FORM STATE
  const [step, setStep] = useState(1);
  const [status, setStatus] = useState('idle');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    companyName: '',
    pageUrl: '' // Stores the present URL
  });

  // Capture the Present URL on Load
  useEffect(() => {
    setFormData(prev => ({ 
      ...prev, 
      pageUrl: window.location.href 
    }));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'mobile') {
      const onlyNums = value.replace(/[^0-9]/g, '');
      if (onlyNums.length <= 10) setFormData({ ...formData, [name]: onlyNums });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  // STEP 1: SAVE PARTIAL DATA & GO TO NEXT
  const handleNext = async (e) => {
    e.preventDefault();
    if (formData.name && formData.email) {
      setStep(2);
      
      // LEAD RECOVERY: Save partial lead to Google Sheet in background
      try {
        fetch(GOOGLE_SCRIPT_URL, {
          method: 'POST',
          mode: 'no-cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...formData, status: 'Partial' }),
        });
      } catch (err) {
        console.error("Partial save failed");
      }
    } else {
      alert("Please fill in your name and email.");
    }
  };

  // STEP 2: SAVE FINAL DATA & DOWNLOAD
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.mobile.length !== 10) {
      alert("Please enter a valid 10-digit mobile number.");
      return;
    }
    if (!formData.companyName) {
      alert("Company Name is required.");
      return;
    }

    setStatus('loading');
    try {
      await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, status: 'Complete' }),
      });

      const link = document.createElement('a');
      link.href = DOWNLOAD_FILE_URL;
      link.setAttribute('download', 'The-Startup-Zone-Ebook.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();

      navigate('/thankyou');
    } catch (error) {
      console.error("Error:", error);
      setStatus('idle');
      alert("Submission failed. Please try again.");
    }
  };

  return (
    <>
      <style>{`
        /* Prefix 'fc-' for Final CTA isolation */
        .fc-section {
          position: relative;
          width: 100%;
          min-height: 80vh; 
          display: flex;
          align-items: center;     /* Perfect vertical center */
          justify-content: center; /* Perfect horizontal center */
          padding: 80px 1.25rem;   
          overflow: hidden;
          background-color: #0b1120;
          box-sizing: border-box;
        }

        .fc-container {
          max-width: 1200px;
          margin: 0 auto;
          position: relative;
          z-index: 10;
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .fc-content {
          text-align: center;
          max-width: 48rem;
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .fc-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 9999px;
          border: 1px solid rgba(45, 212, 191, 0.4);
          background: rgba(45, 212, 191, 0.1);
          padding: 0.35rem 1rem;
          font-size: 0.7rem;
          font-weight: 600;
          color: #2dd4bf; 
          margin-bottom: 1.5rem;
          letter-spacing: 0.05em;
          width: fit-content;
        }

        .fc-title {
          font-size: 2.25rem;
          font-weight: 800;
          line-height: 1.15;
          color: #f8fafc;
          margin: 0 0 1.25rem 0;
        }

        .fc-gradient {
          background: linear-gradient(to right, #2dd4bf, #22d3ee);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .fc-description {
          color: #94a3b8;
          font-size: 1.1rem;
          line-height: 1.6;
          margin-bottom: 2.5rem;
        }

        .fc-card {
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 20px;
          padding: 2rem;
          width: 100%;
          max-width: 36rem;
          margin: 0 auto;
          text-align: left;
        }

        .fc-step-label {
          color: #2dd4bf;
          font-size: 0.7rem;
          font-weight: 700;
          text-transform: uppercase;
          margin-bottom: 1.25rem;
          display: block;
        }

        .fc-row {
          display: flex;
          gap: 1rem;
          width: 100%;
        }

        .fc-input-group { 
          margin-bottom: 1.25rem; 
          flex: 1; 
        }
        
        .fc-input-group label {
          display: block;
          color: #f8fafc;
          font-size: 0.8rem;
          margin-bottom: 0.5rem;
          font-weight: 500;
        }

        .fc-input {
          width: 100%;
          padding: 0.875rem 1rem;
          border-radius: 10px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: white;
          outline: none;
          box-sizing: border-box;
          font-size: 0.9rem;
          transition: border-color 0.3s;
        }

        .fc-input:focus { border-color: #2dd4bf; }

        .fc-btn {
          width: 100%;
          padding: 1rem;
          background: #2dd4bf;
          color: #0b1120;
          border: none;
          border-radius: 10px;
          font-weight: 700;
          cursor: pointer;
          transition: transform 0.2s, opacity 0.2s;
        }

        .fc-btn:hover { transform: translateY(-1px); opacity: 0.95; }

        @media (min-width: 768px) {
          .fc-title { font-size: 3rem; }
          .fc-card { padding: 3rem; }
        }

        @media (max-width: 600px) {
          .fc-row { flex-direction: column; gap: 0; }
          .fc-title { font-size: 1.8rem; }
        }
      `}</style>

      <section className="fc-section" id="get-ebook">
        <motion.div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            x: "-50%",
            y: "-50%",
            width: "600px",
            height: "600px",
            borderRadius: "50%",
            opacity: 0.03,
            filter: "blur(80px)",
            background: "#2dd4bf"
          }}
          animate={{ scale: [1, 1.15, 1], rotate: [0, 90, 180] }}
          transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
        />

        <div className="fc-container">
          <div className="fc-content">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="fc-badge"
            >
              The Startup Zone · Free Ebook
            </motion.span>

            <motion.h2 
              className="fc-title"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Stop learning this{" "}
              <span className="fc-gradient">the expensive way.</span>
            </motion.h2>

            <motion.p 
              className="fc-description"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              Ayaan and Radha made every mistake so you don't have to. 10 chapters. 
              Every legal and financial decision your startup will face. 
              Written specifically for founders building in India.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="fc-card"
            >
              <AnimatePresence mode="wait">
                {step === 1 ? (
                  <motion.form
                    key="step1"
                    initial={{ x: 20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: -20, opacity: 0 }}
                    onSubmit={handleNext}
                  >
                    <span className="fc-step-label">Step 1 of 2: Basics</span>
                    <div className="fc-row">
                      <div className="fc-input-group">
                        <label>First Name</label>
                        <input 
                          required 
                          name="name" 
                          value={formData.name} 
                          onChange={handleChange} 
                          className="fc-input" 
                          placeholder="Name"
                        />
                      </div>
                      <div className="fc-input-group">
                        <label>Email Address</label>
                        <input 
                          required 
                          type="email" 
                          name="email" 
                          value={formData.email} 
                          onChange={handleChange} 
                          className="fc-input" 
                          placeholder="Email"
                        />
                      </div>
                    </div>
                    <button type="submit" className="fc-btn">Next</button>
                  </motion.form>
                ) : (
                  <motion.form
                    key="step2"
                    initial={{ x: 20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: -20, opacity: 0 }}
                    onSubmit={handleSubmit}
                  >
                    <span className="fc-step-label">Step 2 of 2: Company Info</span>
                    <div className="fc-row">
                      <div className="fc-input-group">
                        <label>Mobile Number</label>
                        <input 
                          required 
                          name="mobile" 
                          value={formData.mobile} 
                          onChange={handleChange} 
                          className="fc-input" 
                          placeholder="10-digits"
                        />
                      </div>
                      <div className="fc-input-group">
                        <label>Company Name</label>
                        <input 
                          required 
                          name="companyName" 
                          value={formData.companyName} 
                          onChange={handleChange} 
                          className="fc-input" 
                          placeholder="Company Name"
                        />
                      </div>
                    </div>
                    <button type="submit" className="fc-btn" disabled={status === 'loading'}>
                      {status === 'loading' ? 'Processing...' : 'Submit & Download'}
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setStep(1)} 
                      style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '0.75rem', marginTop: '1.25rem', cursor: 'pointer', width: '100%', textAlign: 'center' }}
                    >
                      ← Back
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default FinalCTA;